import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  DatePicker,
  Flex,
  Form,
  Input,
  message,
  Modal,
  ModalProps
} from 'antd'
import { useParams } from 'react-router-dom'

import {
  usePersonsServiceGetPersonByIdEndpoint,
  usePersonsServiceGetPersonByIdEndpointKey,
  usePersonsServiceUpdatePersonEndpoint
} from 'src/api/queries'
import { ApiError, UpdatePersonRequest } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import {
  dayMonthYearFormatWithDot,
  disabledFutureDate,
  formatDate,
  yearMonthDaySlash
} from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type Props = {
  onCancel: () => void
} & ModalProps

export const PrisonModal = ({ onCancel, ...props }: Props) => {
  const { t } = useLocales()
  const queryClient = useQueryClient()

  const [form] = Form.useForm()

  const { id } = useParams()

  const { data, isLoading } = usePersonsServiceGetPersonByIdEndpoint(
    { id: String(id) },
    [String(id)],
    { enabled: !!id }
  )

  const { mutate, isPending } = usePersonsServiceUpdatePersonEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [usePersonsServiceGetPersonByIdEndpointKey]
      })
      message.success(t('common.save_success'))
      onCancel()
    },
    onError: (e: ApiError) => formErrorHandler(e.body.errors)
  })

  const onSubmit = async () => {
    const values = await form.validateFields()

    const typedData = data as UpdatePersonRequest

    mutate({
      id: String(id),
      requestBody: {
        ...typedData,
        penitentiaryRecords: [
          ...(typedData?.penitentiaryRecords ?? []),
          {
            prisonName: values?.prisonName,
            fromDate: formatDate(
              values?.penitentiaryRecordsDate[0],
              yearMonthDaySlash
            ),
            toDate: formatDate(
              values?.penitentiaryRecordsDate[1],
              yearMonthDaySlash
            )
          }
        ]
      }
    })
  }

  return (
    <Modal
      open
      loading={isLoading}
      maskClosable={false}
      footer={false}
      onCancel={onCancel}
      {...props}
    >
      <Form layout="vertical" form={form}>
        <Flex vertical gap={24}>
          <Form.Item
            name="prisonName"
            label={t('cases.prison')}
            rules={requiredRules}
          >
            <Input placeholder={t('cases.prison')} />
          </Form.Item>
          <Form.Item
            name="penitentiaryRecordsDate"
            label={t('persons.prison_record')}
            rules={requiredRules}
          >
            <DatePicker.RangePicker
              className="full-width"
              format={dayMonthYearFormatWithDot}
              placement="bottomRight"
              disabledDate={disabledFutureDate}
              placeholder={[
                t('notifications.from_date'),
                t('notifications.to_date')
              ]}
            />
          </Form.Item>

          <Flex justify="center" gap={8}>
            <Button onClick={onCancel}>{t('common.cancel')}</Button>
            <Button type="primary" onClick={onSubmit} loading={isPending}>
              {t('common.save')}
            </Button>
          </Flex>
        </Flex>
      </Form>
    </Modal>
  )
}
